import React, { useEffect, useState } from 'react';

import { useSelector } from 'react-redux';

import Avatar from '@atlaskit/avatar';
import Button from '@atlaskit/button/standard-button';
import ChevronDownIcon from '@atlaskit/icon/glyph/chevron-down';
import InlineEdit from '@atlaskit/inline-edit';

import { useIntl } from 'src/hooks/intl';
import { useCheckUserAccess } from 'src/hooks/use-check-user-access';
import { getCurrentPullRequestSourceBranchDetails } from 'src/redux/pull-request/selectors';
import Link from 'src/router/components/link';
import { Ref } from 'src/sections/create-branch';
import { REF_TYPE } from 'src/sections/create-branch/constants';
import { getRef } from 'src/sections/create-branch/utils';
import { BranchSelector } from 'src/sections/repository/components/ref-selector/branch-selector';
import { useRefFetchActions } from 'src/sections/repository/sections/branches/hooks/use-ref-fetch-actions';
import { useRetargetPullRequest } from 'src/sections/repository/sections/pull-request/hooks/retarget-pr';
import { publishUiEvent } from 'src/utils/analytics/publish';

import messages from './branch-info.i18n';
import * as infoStyles from './branch-info.styled';

type Props = {
  branchHref?: string;
  branchName: string;
  repoAvatarSrc?: string;
  repoHref?: string;
  repoName?: string;
  repoOwnerAvatarSrc?: string;
  repoOwnerHref?: string;
  repoOwnerName?: string;
  supportsRetargeting?: boolean;
  closeBranchInfo?: () => void;
};

export const BranchInfo = ({
  branchHref,
  branchName,
  repoAvatarSrc,
  repoHref,
  repoName,
  repoOwnerAvatarSrc,
  repoOwnerHref,
  repoOwnerName,
  supportsRetargeting = false,
  closeBranchInfo,
}: Props) => {
  const intl = useIntl();
  const hasSourceBranchDetails = useSelector(
    getCurrentPullRequestSourceBranchDetails
  );
  const userHasWrite = useCheckUserAccess('write');

  const [destination, setDestination] = useState<Ref>({
    name: branchName,
    type: REF_TYPE.BRANCH,
  });

  const setDestinationWrapper = (branch: Ref | undefined) => {
    if (branch) {
      setDestination(branch);
    }
  };

  const { retargetPullRequest } = useRetargetPullRequest();

  const { fetchRefs, fetchNextRefs, state } = useRefFetchActions({
    sourceBranch: hasSourceBranchDetails?.name,
  });

  useEffect(() => {
    fetchRefs();
  }, [destination, fetchRefs]);

  const publishBranchLinkClick = (linkId: string) => {
    publishUiEvent({
      action: 'clicked',
      actionSubject: 'button',
      actionSubjectId: linkId,
      source: 'branchInfo',
    });
  };

  const publishRetargetClick = () => {
    publishUiEvent({
      action: 'clicked',
      actionSubject: 'button',
      actionSubjectId: 'retarget',
      source: 'prView',
    });
  };

  const renderBranchInfoListElement = (params: {
    leftColumnContent: string;
    rightColumnContent: JSX.Element;
  }) => {
    return (
      <infoStyles.BranchInfoListItem>
        <infoStyles.LeftColumn aria-hidden="true">
          {params.leftColumnContent}
        </infoStyles.LeftColumn>
        <infoStyles.RightColumn>
          {params.rightColumnContent}
        </infoStyles.RightColumn>
      </infoStyles.BranchInfoListItem>
    );
  };

  const renderRepositoryOwner = () => {
    if (!repoOwnerName) {
      return null;
    }

    const repoOwnerAvatar = (
      <Avatar name={repoOwnerName} size="small" src={repoOwnerAvatarSrc} />
    );

    const content = {
      leftColumnContent: intl.formatMessage(messages.repoWorkspaceLabel),
      rightColumnContent: repoOwnerHref ? (
        <Button
          appearance="link"
          href={repoOwnerHref}
          onClick={() => publishBranchLinkClick('repoOwnerHref')}
          // Accessibility bugfix: Anchor links should not have type="button"
          // @ts-ignore -> TODO remove when @atlaskit/button is updated
          type={null}
          iconBefore={repoOwnerAvatar}
          aria-label={intl.formatMessage(messages.openLinkLabel, {
            name: repoOwnerName,
            type: 'workspace',
          })}
        >
          {repoOwnerName}
        </Button>
      ) : (
        <infoStyles.FormerUser>
          <infoStyles.FormerUserAvatar>
            {repoOwnerAvatar}
          </infoStyles.FormerUserAvatar>
          <infoStyles.FormerUserName>{repoOwnerName}</infoStyles.FormerUserName>
        </infoStyles.FormerUser>
      ),
    };
    return renderBranchInfoListElement(content);
  };

  const renderRepositoryName = () => {
    const content = {
      leftColumnContent: intl.formatMessage(messages.repoNameLabel),
      rightColumnContent: (
        <Button
          appearance="link"
          isDisabled={!repoName}
          href={repoHref}
          onClick={() => publishBranchLinkClick('repoHref')}
          // Accessibility bugfix: Anchor links should not have type="button"
          // @ts-ignore -> TODO remove when @atlaskit/button is updated
          type={null}
          iconBefore={
            <Avatar name={repoName} src={repoAvatarSrc} size="small" />
          }
          aria-label={
            repoName
              ? intl.formatMessage(messages.openLinkLabel, {
                  name: repoName,
                  type: 'repository',
                })
              : undefined
          }
        >
          {repoName || intl.formatMessage(messages.deletedRepository)}
        </Button>
      ),
    };
    return renderBranchInfoListElement(content);
  };

  const onCancel = () => {
    const branchRef = getRef({ name: branchName });
    setDestination(branchRef);
  };

  const onConfirm = () => {
    // No need to retarget if the user chooses the same branch as the existing destination
    if (destination.name === branchName) {
      return;
    }
    publishRetargetClick();
    if (closeBranchInfo) {
      closeBranchInfo();
    }
    retargetPullRequest(destination.name);
  };

  const renderBranchReadView = () => {
    return (
      <infoStyles.ReadContainer>
        <infoStyles.LinkContainer>
          <Link
            to={branchHref || ''}
            onClick={() => publishBranchLinkClick('branchHref')}
            aria-label={intl.formatMessage(messages.openLinkLabel, {
              name: branchName,
              type: 'branch',
            })}
            data-testid="branch-info-read"
          >
            <infoStyles.LinkTextContainer>
              <infoStyles.LinkTextSpan>{branchName}</infoStyles.LinkTextSpan>
            </infoStyles.LinkTextContainer>
          </Link>
        </infoStyles.LinkContainer>
        <infoStyles.Icon>
          <ChevronDownIcon label="" />
        </infoStyles.Icon>
      </infoStyles.ReadContainer>
    );
  };

  const renderBranchEditView = () => {
    return (
      <infoStyles.EditContainer>
        <BranchSelector
          branchSelector={state}
          value={{ label: destination.name, value: destination.name }}
          onChangeBranch={setDestinationWrapper}
          fetchRefOptions={fetchRefs}
          fetchNextRefOptions={fetchNextRefs}
          autoFocus
          startMenuOpen
        />
      </infoStyles.EditContainer>
    );
  };

  const renderBranchName = () => {
    const allowRetargeting =
      !!supportsRetargeting && userHasWrite && !!hasSourceBranchDetails;
    const content = {
      leftColumnContent: intl.formatMessage(messages.branchLabel),
      rightColumnContent: allowRetargeting ? (
        <infoStyles.RetargetBranch>
          <InlineEdit
            defaultValue={branchName}
            readViewFitContainerWidth
            keepEditViewOpenOnBlur
            editView={renderBranchEditView}
            readView={renderBranchReadView}
            onConfirm={onConfirm}
            onCancel={onCancel}
          />
        </infoStyles.RetargetBranch>
      ) : (
        <Button
          appearance="link"
          href={branchHref}
          onClick={() => publishBranchLinkClick('branchHref')}
          // Accessibility bugfix: Anchor links should not have type="button"
          // @ts-ignore -> TODO remove when @atlaskit/button is updated
          type={null}
          isDisabled={!branchHref}
          aria-label={intl.formatMessage(messages.openLinkLabel, {
            name: branchName,
            type: 'branch',
          })}
        >
          {branchName}
        </Button>
      ),
    };
    return renderBranchInfoListElement(content);
  };

  return (
    <infoStyles.BranchInfoList role="list">
      {renderRepositoryOwner()}
      {renderRepositoryName()}
      {renderBranchName()}
    </infoStyles.BranchInfoList>
  );
};
